import {
  addCubaCalendarDays,
  cubaMinutesOfDay,
  cubaWallTimeToUtc,
  getCubaTimeParts,
} from '../time/cubaTime';
import {
  getActiveWindow,
  parseTimeToMinutes,
  resolveActiveTurnDayKey,
  resolveResultsFocusDayKey,
  resolveWindowSchedule,
  windowCrossesMidnight,
  type WindowScheduleConfig,
} from './windowSchedule';

/** Most recent Cuba HH:mm at or before nowUtc. */
function mostRecentCubaHHMM(nowUtc: Date, hhmm: string): Date {
  const cuba = getCubaTimeParts(nowUtc);
  const [h, m] = hhmm.split(':').map(Number);
  let candidate = cubaWallTimeToUtc(cuba.year, cuba.month, cuba.day, h, m, 0);
  if (candidate.getTime() > nowUtc.getTime()) {
    const prev = addCubaCalendarDays(cuba.year, cuba.month, cuba.day, -1);
    candidate = cubaWallTimeToUtc(prev.year, prev.month, prev.day, h, m, 0);
  }
  return candidate;
}

function shiftCubaDayKey(dayKey: string, deltaDays: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey ?? '').trim());
  if (!match) return dayKey;
  const next = addCubaCalendarDays(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    deltaDays,
  );
  return `${next.year}-${String(next.month).padStart(2, '0')}-${String(next.day).padStart(2, '0')}`;
}

function windowOpenClose(
  config: WindowScheduleConfig | null | undefined,
  window: 'day' | 'night',
): { open: string; close: string } {
  const schedule = resolveWindowSchedule(config);
  if (window === 'day') {
    return { open: schedule.dayOpen, close: schedule.dayClose };
  }
  return { open: schedule.nightOpen, close: schedule.nightClose };
}

/**
 * Platform catalog dayKey (official draw / close civil day) → house acceptance
 * dayKey used on plays and drawSessions. Overnight turns: acceptance is −1 day.
 */
export function acceptanceDayKeyForPlatformDraw(
  config: WindowScheduleConfig | null | undefined,
  window: 'day' | 'night',
  platformDayKey: string,
): string {
  const { open, close } = windowOpenClose(config, window);
  if (!windowCrossesMidnight(open, close)) return platformDayKey;
  return shiftCubaDayKey(platformDayKey, -1);
}

/**
 * House acceptance dayKey → platform catalog dayKey for the same turn.
 * Overnight turns: platform/publish civil day is +1 day.
 */
export function platformDayKeyForAcceptanceTurn(
  config: WindowScheduleConfig | null | undefined,
  window: 'day' | 'night',
  acceptanceDayKey: string,
): string {
  const { open, close } = windowOpenClose(config, window);
  if (!windowCrossesMidnight(open, close)) return acceptanceDayKey;
  return shiftCubaDayKey(acceptanceDayKey, 1);
}

/**
 * dayKey for the turn whose results-wait block is active now
 * (publish calendar day of the acceptance turn that just closed).
 */
export function resolveResultsWaitTurnDayKey(
  config: WindowScheduleConfig | null | undefined,
  window: 'day' | 'night',
  nowUtc: Date = new Date(),
): string {
  const schedule = resolveWindowSchedule(config);
  const closeHHMM = window === 'day' ? schedule.dayClose : schedule.nightClose;
  const closeUtc = mostRecentCubaHHMM(nowUtc, closeHHMM);
  const duringAcceptance = new Date(closeUtc.getTime() - 60_000);
  return resolveResultsFocusDayKey(config, window, duringAcceptance);
}

/**
 * Acceptance dayKey for the turn that most recently closed (matches play stamps).
 * Overnight day closing at midday uses the previous evening's open calendar day —
 * not the lottery draw's civil date (avoids colliding with the next evening open).
 */
export function resolveClosedTurnAcceptanceDayKey(
  config: WindowScheduleConfig | null | undefined,
  window: 'day' | 'night',
  nowUtc: Date = new Date(),
): string {
  const schedule = resolveWindowSchedule(config);
  const closeHHMM = window === 'day' ? schedule.dayClose : schedule.nightClose;
  const closeUtc = mostRecentCubaHHMM(nowUtc, closeHHMM);
  const duringAcceptance = new Date(closeUtc.getTime() - 60_000);
  return resolveActiveTurnDayKey(config, window, duringAcceptance);
}

/**
 * When the currently active acceptance window opened (Cuba wall → UTC).
 * Null when that window is not accepting right now.
 */
export function resolveActiveWindowOpenedAt(
  config: WindowScheduleConfig | null | undefined,
  window: 'day' | 'night',
  nowUtc: Date = new Date(),
): Date | null {
  if (getActiveWindow(config, nowUtc) !== window) return null;
  const schedule = resolveWindowSchedule(config);
  const openHHMM = window === 'day' ? schedule.dayOpen : schedule.nightOpen;
  const closeHHMM = window === 'day' ? schedule.dayClose : schedule.nightClose;
  const [h, m] = openHHMM.split(':').map(Number);
  const cuba = getCubaTimeParts(nowUtc);
  const cur = cubaMinutesOfDay(nowUtc);
  const close = parseTimeToMinutes(closeHHMM);

  if (windowCrossesMidnight(openHHMM, closeHHMM) && cur < close) {
    const prev = addCubaCalendarDays(cuba.year, cuba.month, cuba.day, -1);
    return cubaWallTimeToUtc(prev.year, prev.month, prev.day, h, m, 0);
  }
  return cubaWallTimeToUtc(cuba.year, cuba.month, cuba.day, h, m, 0);
}

function settledAtMillis(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'object' && value !== null && 'toMillis' in value) {
    const ts = value as { toMillis?: () => number };
    if (typeof ts.toMillis === 'function') return ts.toMillis();
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}

/**
 * True when drawSession.settledAt is from a prior turn that reused the same
 * acceptance dayKey (overnight collision). New opens must not be rejected or
 * auto-expired against that stale settlement.
 */
export function isStaleSettledSession(
  settledAt: unknown,
  config: WindowScheduleConfig | null | undefined,
  window: 'day' | 'night',
  nowUtc: Date = new Date(),
): boolean {
  const openedAt = resolveActiveWindowOpenedAt(config, window, nowUtc);
  const settledMs = settledAtMillis(settledAt);
  if (!openedAt || settledMs == null) return false;
  return settledMs < openedAt.getTime();
}
